import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./CategoryTabs.css";
import URL from "./Url";

// Renders placeholder tab buttons while categories are fetching
const SkeletonTabs = () => (
  <div className="category-tabs-container category-tabs-container--skeleton">
    <div className="category-tabs">
      {[88, 72, 96, 80, 68].map((width, i) => (
        <div
          key={i}
          className="tab-button tab-button--skeleton"
          style={{ minWidth: width }}
        />
      ))}
    </div>
  </div>
);

const CategoryTabs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const tabsRef = useRef(null);
  const underlineRef = useRef(null);
  const [visibleTabs, setVisibleTabs] = useState(5);
  const [activeTab, setActiveTab] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${URL}/api/categories`);
        const data = await res.json();
        const tabCategories = data.filter((cat) => cat.showInTabs);
        setCategories(tabCategories);
        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  // Handle tab click
  const handleTabClick = (keyword) => {
    setActiveTab(keyword);
    navigate(`/search/${keyword}/page/1`);
  };

  // Adjust visible tabs based on window size
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 768;
      if (!isMobile) {
        setVisibleTabs(categories.length);
      } else {
        const container = tabsRef.current;
        if (container) {
          const containerWidth = container.clientWidth;
          const tabWidth = 80;
          const maxTabs = Math.floor(containerWidth / tabWidth);
          setVisibleTabs(Math.min(maxTabs, categories.length));
        }
      }
    };
    if (!loading) {
      window.addEventListener("resize", handleResize);
      handleResize();
    }
    return () => window.removeEventListener("resize", handleResize);
  }, [categories.length, loading]);

  // Set active tab from URL
  useEffect(() => {
    const pathParts = location.pathname.split("/");
    const currentKeyword = pathParts[2];
    if (currentKeyword && categories.some((category) => category.keyword === currentKeyword)) {
      setActiveTab(currentKeyword);
    } else {
      setActiveTab(null);
    }
  }, [location, categories]);

  // Move sliding underline to active tab
  useEffect(() => {
    const activeEl = document.querySelector(".tab-button.active");
    const underline = underlineRef.current;

    if (activeEl && underline) {
      underline.style.width = `${activeEl.offsetWidth}px`;
      underline.style.left = `${activeEl.offsetLeft}px`;

      const container = tabsRef.current;
      if (container) {
        const containerRect = container.getBoundingClientRect();
        const activeRect = activeEl.getBoundingClientRect();
        const offset = activeRect.left - containerRect.left - container.clientWidth / 2 + activeRect.width / 2;
        container.scrollBy({ left: offset, behavior: "smooth" });
      }
    }
  }, [activeTab, categories, visibleTabs]);

  if (loading) return <SkeletonTabs />;
  if (categories.length === 0) return null;

  return (
    <div className="category-tabs-container" ref={tabsRef}>
      <div className="category-tabs">
        {categories.slice(0, visibleTabs).map((category) => (
          <button
            key={category.keyword}
            className={`tab-button ${activeTab === category.keyword ? "active" : ""}`}
            onClick={() => handleTabClick(category.keyword)}
          >
            {category.name}
          </button>
        ))}
        <span className="underline" ref={underlineRef}></span>
      </div>
    </div>
  );
};

export default CategoryTabs;